import { Col, Form, Input, Modal, Row } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { discountCodeCollectionSelector } from "../../../../../modules/collection/collection.reducers";
import { setPOSDiscountCodes } from "../../../../../modules/order/order.actions";
import { useAppCtx } from "../../../../../providers/app.provider";
import posCartService from "../../../../../services/pos/pos-cart.services";
import reduxService from "../../../../../services/redux.services";
import { addEventClickAndStretchToScrollY } from "../../../../../utils/helpers";
import { HttpStatusCode } from "../../../../../utils/http-common";
import { CheckCircleIcon, DiscountCodeEmptyIcon, WarningTriangle } from "../../../../assets/icons.constants";
import { DiscountCodeCard } from "../../../../components/discount-code-card/discount-code-card.component";
import { EnumDiscountCodeResponseCode } from "../../../../constants/enums";
import "./DiscountCodeDialog.scss";

/**
 * Use for QR-Order
 * @param {*} props
 * @returns
 */
function DiscountCodeDialog(props) {
  const { t } = useTranslation();
  const { Toast, fontFamily } = useAppCtx();
  const discountCodeCollectionInRedux = useSelector(discountCodeCollectionSelector);
  const [discountCodeValue, setDiscountCodeValue] = useState("");

  const {
    isVisible = false,
    onCancel,
    className,
    title,
    posDiscountCodes,
    isShowDiscountCodeCollection = true,
    setIsLoading,
    orderId,
    setIsChangeDiscountCode,
  } = props;
  const discountCodeFieldName = "discountCode";
  const elementId = "list-discount-code";
  const [form] = Form.useForm();
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (message !== "") {
      setTimeout(() => {
        setMessage("");
      }, 3000);
    }
  }, [message]);

  function handleCancel() {
    form.setFieldValue(discountCodeFieldName, "");
    if (onCancel) {
      onCancel();
    }
  }

  function onChangeInput(e) {
    if (window?.changeInput) {
      clearTimeout(window.changeInput);
    }
    window.changeInput = setTimeout(() => {
      setDiscountCodeValue(e?.target?.value);
    }, 300);
  }

  async function applyDiscountCode(discountCode) {
    await applyDiscountCodes([...posDiscountCodes, discountCode]);
  }

  async function applyDiscountCodes(discountCodes, isShowToatSuccess = true) {
    setIsLoading(true);
    const request = {
      orderId: orderId,
      discountCodes: discountCodes ?? [],
    };
    const response = await posCartService.applyDiscountCodesToOrderAsync(request);
    setIsLoading(false);
    const { succeeded, message, data } = response?.data;
    if (response?.status === HttpStatusCode.Ok) {
      const discountCodesApplied = data?.discountCodes ?? [];
      reduxService.dispatch(setPOSDiscountCodes(discountCodesApplied));
      setIsChangeDiscountCode(true);
      if (succeeded) {
        if (isShowToatSuccess) {
          Toast.show({
            messageType: "success",
            message: t("messages.appliedDiscountCodeSuccessfully", "Áp dụng mã giảm giá thành công"),
            icon: <CheckCircleIcon />,
            placement: "top",
            duration: 3,
          });
        }
      } else {
        Toast.show({
          messageType: "warning",
          message: t(message, { remainingAmount: data?.extraData?.remainingAmount }),
          icon: <WarningTriangle />,
          placement: "top",
          duration: 3,
        });
      }
    } else {
      Toast.show({
        messageType: "warning",
        message: t(message),
        icon: <WarningTriangle />,
        placement: "top",
        duration: 3,
      });
    }
  }

  const handleClickUse = (code) => {
    let POSDiscountCodes = reduxService.getPOSDiscountCodes();
    const indexExist = POSDiscountCodes?.findIndex((discountCode) => discountCode === code);
    if (indexExist === -1) {
      POSDiscountCodes = [...POSDiscountCodes, code];
      applyDiscountCodes(POSDiscountCodes);
    } else {
      POSDiscountCodes.splice(indexExist, 1);
      applyDiscountCodes(POSDiscountCodes, false);
    }
  };

  async function onApply() {
    const code = form.getFieldValue(discountCodeFieldName)?.toUpperCase()?.trim();
    if (code) {
      try {
        const reponse = await posCartService.verifyDisCountCodeAsync(code);
        const responseCode = reponse?.discountCodeResult?.responseCode;
        const indexExist = discountCodeCollectionInRedux?.findIndex((item) => item?.code === code);
        if (
          responseCode === EnumDiscountCodeResponseCode.Success ||
          responseCode === EnumDiscountCodeResponseCode.MinimumPurchaseValue ||
          responseCode === EnumDiscountCodeResponseCode.Existed
        ) {
          if (indexExist !== -1 && posDiscountCodes?.includes(code)) {
            setMessage(t("promotion.discountCode.description.existed", "Mã giảm giá đã tồn tại."));
          } else {
            posCartService.addDiscountCodeCollection(reponse?.discountCodeResult?.result);
            applyDiscountCode(code);
            form.setFieldValue(discountCodeFieldName, "");
          }
        } else {
          setMessage(t(reponse?.discountCodeResult?.responseMessage));
        }
      } catch (err) {}
    } else {
      setMessage(t("promotion.discountCode.description.notFound", "Mã giảm giá không hợp lệ. Vui lòng thử lại."));
    }
  }

  useEffect(() => {
    addEventClickAndStretchToScrollY(elementId);
  }, []);

  return (
    <Modal
      wrapClassName={className}
      closable={true}
      className="discount-code-dialog"
      title={title ?? t("checkOutPage.promotion", "Khuyến mãi")}
      open={isVisible}
      onCancel={handleCancel}
      footer={false}
      getPopupContainer={(trigger) => trigger.parentNode}
      style={{ fontFamily: fontFamily }}
      forceRender={true}
    >
      <Row className="discount-code-dialog-body" wrap={false}>
        <Col flex={"auto"} className="input-group">
          <Form form={form}>
            <Form.Item name={discountCodeFieldName} className="form-item-custom">
              <Input
                className="discount-code-input"
                type="text"
                placeholder={t("promotion.discountCode.placeHolderPromotion", "Nhập mã giảm giá")}
                onChange={onChangeInput}
              />
            </Form.Item>
          </Form>
          {message !== "" && <div className="message">{message}</div>}
        </Col>
        <Col
          flex={"no"}
          className={`apply-button ${discountCodeValue?.length > 0 ? "" : "disable"} `}
          onClick={discountCodeValue?.length > 0 ? onApply : () => {}}
        >
          {t("loyaltyPoint.apply", "Áp dụng")}
        </Col>
      </Row>
      <Row className="list-discount-code" id={elementId}>
        {isShowDiscountCodeCollection && discountCodeCollectionInRedux?.length > 0 ? (
          discountCodeCollectionInRedux
            ?.map((item) => {
              return { ...item, isActive: posDiscountCodes?.some((discountCode) => discountCode === item?.code) };
            })
            ?.sort((a, b) => {
              const aValue = a?.isActive ? 1 : 0;
              const bValue = b?.isActive ? 1 : 0;
              return bValue - aValue;
            })
            ?.map((item, index) => {
              return (
                <DiscountCodeCard
                  key={index}
                  data={item}
                  colorConfig={{}}
                  isShowRedeem={false}
                  isHomepage={false}
                  onClickRedeem={false}
                  isCheckOutPage={true}
                  onClickUse={handleClickUse}
                  isActive={item?.isActive}
                  isApply={item?.isApply ?? true}
                  hiddenDiscount={() => {}}
                />
              );
            })
        ) : (
          <div className="discount-code-colection-empty">
            <DiscountCodeEmptyIcon className="icon" />
            <div className="text">
              {t("promotion.discountCode.description.YouDoNotHaveAnyPromotions", "Bạn chưa có khuyến mãi nào")}
            </div>
          </div>
        )}
      </Row>
    </Modal>
  );
}

export default DiscountCodeDialog;
